import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/helpers";
import { CalendarClock, AlertTriangle, CalendarPlus, Hand, CheckCircle2, ArrowRight, Bell, Activity, ShieldAlert } from "lucide-react";

const RESULT_COLOR = {
  pmt_ok: "bg-green-500", pmt_non_signee: "bg-orange-500", pmt_non_realisee: "bg-red-500",
  refus: "bg-red-500", a_demander: "bg-accent-500", revenir_plus_tard: "bg-orange-500", autre: "bg-accent-500",
  cloture_manuel: "bg-green-500", affectation: "bg-accent-500", prise_en_charge: "bg-slate-500", non_conforme: "bg-red-500",
};

export default function Dashboard() {
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [history, setHistory] = useState([]);
  const [eq, setEq] = useState(null);

  useEffect(() => {
    api.get("/dashboard/counters").then((r) => setC(r.data)).catch(() => {});
    api.get("/history/recent").then((r) => setHistory((Array.isArray(r.data) ? r.data : []).slice(0, 8))).catch(() => {});
    api.get("/vehicle-equipment/summary").then((r) => setEq(r.data)).catch(() => {});
  }, []);

  const main = [
    { key: "to_recover_today", label: "À récupérer aujourd'hui", icon: CalendarClock, cls: "bg-brand-800 text-white", iconCls: "bg-accent-500/30 text-accent-200", filter: "today" },
    { key: "overdue", label: "En retard", icon: AlertTriangle, cls: c && c.overdue > 0 ? "bg-white border-2 border-red-300 text-red-700" : "bg-white border border-slate-200 text-slate-800", iconCls: c && c.overdue > 0 ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500", filter: "overdue" },
    { key: "upcoming", label: "À venir", icon: CalendarPlus, cls: "bg-white border border-slate-200 text-slate-800", iconCls: "bg-accent-50 text-accent-700", filter: "upcoming" },
  ];
  const secondary = [
    { key: "assigned", label: "Pris en charge", icon: Hand, cls: "text-slate-600 bg-slate-100" },
    { key: "urgent", label: "Urgents (échéance)", icon: AlertTriangle, cls: "text-red-600 bg-red-50" },
    { key: "recovered_today", label: "Récupérés aujourd'hui", icon: CheckCircle2, cls: "text-green-600 bg-green-50" },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-heading font-extrabold text-brand-900">Tableau de bord</h1>
        <p className="text-slate-500 mt-1">Suivi de la récupération des documents manquants.</p>
      </div>

      {c && (c.to_recover_today > 0 || c.overdue > 0) && (
        <div data-testid="daily-alert-banner" className={`flex items-start gap-3 rounded-xl p-4 border ${c.overdue > 0 ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"}`}>
          <Bell className={`w-5 h-5 mt-0.5 shrink-0 ${c.overdue > 0 ? "text-red-600" : "text-orange-600"}`} />
          <p className={`font-semibold ${c.overdue > 0 ? "text-red-800" : "text-orange-800"}`}>
            {c.to_recover_today} document{c.to_recover_today > 1 ? "s" : ""} à récupérer aujourd'hui
            {c.overdue > 0 && <> · <span className="text-red-700">{c.overdue} en retard</span></>}
            {c.overdue > 0 && <span className="font-normal text-red-700/80"> — pensez à les réaffecter à un chauffeur qui passe dans l'établissement.</span>}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {main.map((m) => (
          <button key={m.key} data-testid={`counter-${m.key}`} onClick={() => nav(`/app/demandes?bucket=${m.filter}`)}
            className={`rounded-2xl ${m.cls} p-6 text-left shadow-sm card-lift`}>
            <div className={`w-11 h-11 rounded-xl ${m.iconCls} flex items-center justify-center mb-4`}><m.icon className="w-6 h-6" /></div>
            <p className="text-5xl font-heading font-extrabold">{c ? c[m.key] : "…"}</p>
            <p className="mt-1 font-semibold opacity-90">{m.label}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {secondary.map((s) => (
          <div key={s.key} data-testid={`counter-${s.key}`} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${s.cls} flex items-center justify-center shrink-0`}><s.icon className="w-5 h-5" /></div>
            <div><p className="text-2xl font-heading font-extrabold text-brand-900 leading-none">{c ? c[s.key] : "…"}</p><p className="text-xs text-slate-500 mt-1 leading-tight">{s.label}</p></div>
          </div>
        ))}
      </div>

      {eq?.enabled && (
        <button type="button" data-testid="equipment-dashboard-block" onClick={() => nav("/app/vehicules?echeances=1")} className={`rounded-xl border p-4 shadow-sm flex items-center gap-4 text-left card-lift ${eq.expire > 0 ? "bg-red-50 border-red-200" : eq.proche > 0 ? "bg-orange-50 border-orange-200" : "bg-white border-slate-200"}`}>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${eq.expire > 0 ? "bg-red-100 text-red-700" : eq.proche > 0 ? "bg-orange-100 text-orange-700" : "bg-green-50 text-green-700"}`}><ShieldAlert className="w-5 h-5" /></div>
          <div className="flex-1"><p className="font-heading font-bold text-brand-900">Échéances véhicules</p>
            <p className="text-sm text-slate-600" data-testid="equipment-dashboard-text">{eq.proche + eq.expire === 0 ? "Tous les équipements suivis sont à jour." : <>{eq.proche > 0 && <span className="text-orange-800 font-semibold">{eq.proche} équipement{eq.proche > 1 ? "s" : ""} arrive{eq.proche > 1 ? "nt" : ""} à échéance</span>}{eq.proche > 0 && eq.expire > 0 && " · "}{eq.expire > 0 && <span className="text-red-700 font-semibold">{eq.expire} équipement{eq.expire > 1 ? "s" : ""} expiré{eq.expire > 1 ? "s" : ""}</span>}</>}</p></div>
          <ArrowRight className="w-4 h-4 text-slate-400" />
        </button>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-heading font-bold text-lg text-brand-900 flex items-center gap-2"><Activity className="w-5 h-5 text-accent-600" />Activité récente</h2>
          <button data-testid="view-all-requests" onClick={() => nav("/app/demandes")} className="text-sm font-semibold text-accent-700 flex items-center gap-1 hover:gap-2 transition-all">
            Voir les demandes <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {history.length === 0 && <p className="p-8 text-center text-slate-400">Aucune activité pour le moment.</p>}
          {history.map((h) => (
            <div key={h.id} className="flex items-center gap-3 p-4 hover:bg-slate-50/60">
              <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${RESULT_COLOR[h.result] || "bg-slate-400"}`} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm">{h.result_label} · <span className="text-slate-500 font-normal">{h.patient_name}</span></p>
                <p className="text-xs text-slate-400">{h.establishment_name} · {h.driver_name}{h.next_date ? ` · revenir le ${fmtDate(h.next_date)}` : ""}</p>
              </div>
              <span className="text-xs text-slate-400 shrink-0">{fmtDateTime(h.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
